import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FacultyLayout } from '../../components/FacultyLayout';
import { Card, CardContent } from '../../components/ui/card';
import { BookOpen, FileText, Upload, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacultyDashboard = () => {
    const { token } = useAuth();
    const [stats, setStats] = useState({
        courses: 0,
        syllabus: 0,
        materials: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const headers = { Authorization: `Bearer ${token}` };
                const [coursesRes, syllabusRes, materialsRes] = await Promise.all([
                    axios.get(`${API_URL}/api/faculty/my-courses`, { headers }),
                    axios.get(`${API_URL}/api/faculty/my-syllabus`, { headers }),
                    axios.get(`${API_URL}/api/faculty/my-materials`, { headers })
                ]);

                setStats({
                    courses: coursesRes.data.length,
                    syllabus: syllabusRes.data.length,
                    materials: materialsRes.data.length
                });
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [token]);

    const statCards = [
        {
            title: 'My Courses',
            value: stats.courses,
            icon: BookOpen,
            color: 'bg-primary',
            link: '/faculty-portal/courses'
        },
        {
            title: 'Syllabus Uploaded',
            value: stats.syllabus,
            icon: FileText,
            color: 'bg-green-500',
            link: '/faculty-portal/syllabus'
        },
        {
            title: 'Study Materials',
            value: stats.materials,
            icon: Upload,
            color: 'bg-purple-500',
            link: '/faculty-portal/materials'
        }
    ];

    return (
        <FacultyLayout title="Dashboard">
            <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid md:grid-cols-3 gap-6">
                    {statCards.map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <Card key={stat.title} className="card-hover">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                                            <p className="text-3xl font-heading font-bold mt-2">
                                                {loading ? '...' : stat.value}
                                            </p>
                                        </div>
                                        <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Quick Actions */}
                <Card>
                    <CardContent className="p-6">
                        <h3 className="text-xl font-heading font-semibold mb-4">Quick Actions</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <a
                                href="/faculty-portal/syllabus"
                                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-center"
                            >
                                <FileText className="w-8 h-8 mx-auto mb-2 text-primary" />
                                <p className="font-medium">Upload Syllabus</p>
                            </a>
                            <a
                                href="/faculty-portal/materials"
                                className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors text-center"
                            >
                                <Upload className="w-8 h-8 mx-auto mb-2 text-primary" />
                                <p className="font-medium">Upload Study Material</p>
                            </a>
                        </div>
                    </CardContent>
                </Card>

                {/* Welcome Message */}
                <Card>
                    <CardContent className="p-6">
                        <h3 className="text-xl font-heading font-semibold mb-2">Welcome to Faculty Portal</h3>
                        <p className="text-muted-foreground">
                            Manage your courses, upload syllabi, and share study materials with students.
                            Use the navigation menu to access different sections of the portal.
                        </p>
                    </CardContent>
                </Card>
            </div>
        </FacultyLayout>
    );
};

export default FacultyDashboard;
